import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Pencil, ShieldCheck, Coins, BookOpen, Loader2, Wallet, CheckCircle2,
  CircleDashed, UserPlus, Sparkles, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { editingApi } from '@/lib/api';
import { bookstoreApi } from '@/lib/bookstoreApi';
import { useAuth } from '@/lib/auth_context';
import { usePW } from '@/lib/store';
import PublishBookForm from './PublishBookForm';

function useStatusLabels(t) {
  return {
    awaiting_payment: { label: t('publish.statusAwaitingPayment'), tone: 'amber',  icon: Coins },
    paid:             { label: t('publish.statusPaid'), tone: 'blue', icon: ShieldCheck },
    in_review:        { label: t('publish.statusInReview'), tone: 'violet', icon: UserPlus },
    completed:        { label: t('publish.statusCompleted'), tone: 'emerald', icon: CheckCircle2 },
    published:        { label: t('publish.statusPublished'), tone: 'emerald', icon: BookOpen },
    cancelled:        { label: t('publish.statusCancelled'), tone: 'zinc', icon: CircleDashed },
  };
}

function StatusBadge({ status, t }) {
  const STATUS_LABELS = useStatusLabels(t);
  const cfg = STATUS_LABELS[status] || { label: status, tone: 'zinc', icon: CircleDashed };
  const Icon = cfg.icon;
  const tone = {
    amber:   'bg-amber-100 text-amber-900 border-amber-300',
    blue:    'bg-blue-100 text-blue-900 border-blue-300',
    violet:  'bg-violet-100 text-violet-900 border-violet-300',
    emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300',
    zinc:    'bg-zinc-200 text-zinc-700 border-zinc-300',
  }[cfg.tone];
  return (
    <Badge className={`border ${tone} gap-1`} data-testid="editing-status-badge">
      <Icon className="h-3 w-3" /> {cfg.label}
    </Badge>
  );
}

export function PublishToBookstoreDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const { project } = usePW();
  const { user, refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [request, setRequest] = useState(null);
  const [authorNotes, setAuthorNotes] = useState('');

  // Catalogs surfaced by the completed-state publish form.
  const [genres, setGenres] = useState([]);
  const [coverTiers, setCoverTiers] = useState([]);
  const [customOrder, setCustomOrder] = useState(null);
  const [languages, setLanguages] = useState([]);

  const reload = async () => {
    if (!project) return;
    setLoading(true);
    try {
      const [q, s, g, ct, langs, order] = await Promise.all([
        editingApi.quote(project.id),
        editingApi.status(project.id),
        bookstoreApi.catalog().catch(() => ({ genres: [] })),
        editingApi.coverTiers().catch(() => ({ tiers: [] })),
        bookstoreApi.languages().catch(() => ({ languages: [] })),
        editingApi.coverOrderStatus(project.id).catch(() => ({ order: null })),
      ]);
      setQuote(q);
      setRequest(s.request || null);
      setGenres(g.genres || []);
      setCoverTiers(ct.tiers || []);
      setLanguages(langs.languages || []);
      setCustomOrder(order?.order || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('publish.loadStatusFailed'));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) reload(); }, [open, project?.id]);

  const status = request?.status;
  const tier = quote?.tier;
  const tokens = request?.tokens_quoted ?? tier?.tokens ?? 0;
  const wordCount = quote?.word_count ?? 0;
  const balance = user?.tokens_purchased ?? quote?.wallet_balance ?? 0;
  const insufficient = balance < tokens;

  const onRequest = async () => {
    setBusy(true);
    try {
      const r = await editingApi.request(project.id, authorNotes);
      setRequest(r.request);
      toast.success(t('publish.requestCreated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('publish.requestFailed'));
    } finally { setBusy(false); }
  };

  const onPay = async () => {
    if (!request) return;
    setBusy(true);
    try {
      const r = await editingApi.pay(request.id);
      setRequest(r.request);
      toast.success(t('publish.tokensDeducted', { count: r.tokens_deducted.toLocaleString() }));
      await refresh?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.error === 'insufficient_tokens') {
        toast.error(t('publish.needTokensShort', { required: d.required.toLocaleString(), shortfall: d.shortfall.toLocaleString() }));
      } else {
        toast.error(typeof d === 'string' ? d : t('publish.paymentFailed'));
      }
    } finally { setBusy(false); }
  };

  const onCancel = async () => {
    if (!request) return;
    setBusy(true);
    try {
      await editingApi.cancel(request.id);
      toast.success(t('publish.requestCancelled'));
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('publish.cancelFailed'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="editing-publish-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            {t('publish.dialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('publish.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('publish.loading')}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Manuscript summary */}
            <div className="rounded-md border border-border bg-panel-header/40 p-3">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-[13px] font-semibold">{project?.name || t('notebook.untitled')}</div>
                  <div className="text-[11px] text-muted-foreground">{project?.author}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-stat text-[14px] font-semibold" data-testid="editing-quote-word-count">
                    {wordCount.toLocaleString()} words
                  </div>
                  <div className="text-[11px] text-muted-foreground">{t('publish.totalManuscript')}</div>
                </div>
              </div>
              {tier && (
                <div className="mt-2 grid grid-cols-3 gap-2 text-[11.5px]">
                  <div className="rounded-sm border border-border bg-background p-2">
                    <div className="text-muted-foreground">{t('publish.tier')}</div>
                    <div className="font-semibold" data-testid="editing-quote-tier-name">{tier.name}</div>
                  </div>
                  <div className="rounded-sm border border-border bg-background p-2">
                    <div className="text-muted-foreground">{t('publish.upTo')}</div>
                    <div className="font-mono-stat">{tier.max_words.toLocaleString()} words</div>
                  </div>
                  <div className="rounded-sm border border-border bg-background p-2">
                    <div className="text-muted-foreground">{t('publish.quoted')}</div>
                    <div className="font-mono-stat font-semibold" data-testid="editing-quote-tokens">
                      {tokens.toLocaleString()} tokens
                    </div>
                  </div>
                </div>
              )}
              {tier?.desc && (
                <p className="mt-2 text-[11.5px] text-muted-foreground">{tier.desc}</p>
              )}
              {!tier && (
                <div className="mt-2 rounded-sm border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11.5px] text-amber-900">
                  {t('publish.exceedsEveryTier')}
                </div>
              )}
            </div>

            {/* Current status */}
            {request && (
              <div className="rounded-md border border-border bg-card p-3">
                <div className="flex items-center justify-between">
                  <StatusBadge status={status} t={t} />
                  <span className="font-mono-stat text-[11px] text-muted-foreground">{request.id}</span>
                </div>
                {request.editor_notes && status === 'completed' && (
                  <div className="mt-2 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-[11.5px] text-emerald-900">
                    <b>{t('publish.editorsNotes')}</b> {request.editor_notes}
                  </div>
                )}
              </div>
            )}

            {/* No request yet — show CTA */}
            {!request && tier && (
              <div className="space-y-2">
                <Label className="text-[11.5px]">{t('publish.notesForEditor')}</Label>
                <Textarea rows={3}
                          data-testid="editing-author-notes"
                          placeholder={t('publish.notesPlaceholder')}
                          value={authorNotes} onChange={e => setAuthorNotes(e.target.value)} />
                <Button onClick={onRequest} disabled={busy} className="w-full gap-1.5"
                        data-testid="editing-request-btn">
                  <Pencil className="h-3.5 w-3.5" /> {t('publish.requestEditing')}
                </Button>
              </div>
            )}

            {/* Awaiting payment */}
            {status === 'awaiting_payment' && (
              <div className="space-y-2">
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Wallet className="h-3.5 w-3.5" /> {t('publish.tokenWallet')}
                    </span>
                    <span className="font-mono-stat font-semibold" data-testid="editing-wallet-balance">
                      {balance.toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[12px]">
                    <span className="text-muted-foreground">{t('publish.afterPayment')}</span>
                    <span className={`font-mono-stat font-semibold ${insufficient ? 'text-destructive' : 'text-emerald-700'}`}>
                      {(balance - tokens).toLocaleString()}
                    </span>
                  </div>
                </div>
                {insufficient && (
                  <div className="rounded-sm border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-[11.5px] text-destructive">
                    {t('publish.walletShort', { amount: (tokens - balance).toLocaleString() })}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={onCancel} disabled={busy}
                          data-testid="editing-cancel-btn">
                    {t('publish.cancelRequest')}
                  </Button>
                  <Button className="flex-1 gap-1.5"
                          onClick={onPay} disabled={busy || insufficient}
                          data-testid="editing-pay-btn">
                    <Coins className="h-3.5 w-3.5" /> {t('publish.payTokens', { count: tokens.toLocaleString() })}
                  </Button>
                </div>
              </div>
            )}

            {/* Paid / In review */}
            {['paid', 'in_review'].includes(status) && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-[12px] text-blue-900">
                <div className="flex items-center gap-1.5 font-semibold">
                  <Sparkles className="h-3.5 w-3.5" />
                  {status === 'paid'
                    ? t('publish.paymentReceived')
                    : t('publish.editorReviewing')}
                </div>
                <p className="mt-1 text-[11.5px] text-blue-900/80">
                  {t('publish.notifiedByEmail')}
                </p>
              </div>
            )}

            {/* Completed — publish form (extracted into PublishBookForm in iter-53o) */}
            {status === 'completed' && (
              <PublishBookForm
                project={project}
                coverTiers={coverTiers}
                genres={genres}
                languages={languages}
                customOrder={customOrder}
                onPublished={reload}
              />
            )}

            {/* Published */}
            {status === 'published' && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-[12px] text-emerald-900">
                <div className="flex items-center gap-1.5 font-semibold">
                  <BookOpen className="h-4 w-4" /> {t('publish.liveInBookstore')}
                </div>
                {request?.bookstore_book_id && (
                  <div className="mt-1 font-mono-stat text-[11px]">{request.bookstore_book_id}</div>
                )}
              </div>
            )}

            {/* Locked-state explanation for non-completed states */}
            {request && !['completed', 'published'].includes(status) && (
              <div className="rounded-sm border border-border bg-panel-header/40 px-2 py-1.5 text-[11.5px] text-muted-foreground flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" />
                {t('publish.unlocksAfterEditing')}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  data-testid="editing-close-btn">{t('publish.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
